import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { faClose, faMoneyBillTransfer } from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { EPixKeyType } from "../../Enum/EPixKeyType"
import { ResellerProfile } from "../../hooks/Reseller/useGetResellerProfile"
import { useCompleteResellerProfile } from "../../hooks/Reseller/useCompleteResellerProfile"

interface CompleteResellerProfilePopupProps {
  profile: ResellerProfile
  onClose: () => void
  onSuccess: () => void
}

export const CompleteResellerProfilePopup: React.FC<CompleteResellerProfilePopupProps> = ({
  profile,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation("reseller")

  const [pixKeyType, setPixKeyType] = useState<EPixKeyType>(profile.pixKeyType ?? EPixKeyType.Undefined)

  const [pixKey, setPixKey] = useState(profile.pixKey ?? "")

  const [bankName, setBankName] = useState(profile.bankName ?? "")

  const [bankAgency, setBankAgency] = useState(profile.bankAgency ?? "")

  const [bankAccount, setBankAccount] = useState(profile.bankAccount ?? "")

  const [bankAccountHolderName, setBankAccountHolderName] = useState(profile.bankAccountHolderName ?? "")

  const { handleCompleteResellerProfile } = useCompleteResellerProfile()

  const pixKeyTypeOptions = [
    { label: t("completeProfile.pix_key_type_undefined"), value: String(EPixKeyType.Undefined) },
    { label: t("completeProfile.pix_key_type_cpf"), value: String(EPixKeyType.Cpf) },
    { label: t("completeProfile.pix_key_type_cnpj"), value: String(EPixKeyType.Cnpj) },
    { label: t("completeProfile.pix_key_type_email"), value: String(EPixKeyType.Email) },
    { label: t("completeProfile.pix_key_type_phone"), value: String(EPixKeyType.Phone) },
    { label: t("completeProfile.pix_key_type_random"), value: String(EPixKeyType.Random) },
  ]

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    handleCompleteResellerProfile(
      {
        pixKeyType,
        pixKey,
        bankName,
        bankAgency,
        bankAccount,
        bankAccountHolderName,
      },
      () => {
        onSuccess()
        onClose()
      },
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("completeProfile.title")}
              icon={faMoneyBillTransfer}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.reseller.completeresellerprofilepopup.1"
                    variant="global"
                    title={t("helpPopup:global.resellerCompleteProfile.title")}
                    description={t("helpPopup:global.resellerCompleteProfile.description")}
                    bullets={
                      t("helpPopup:global.resellerCompleteProfile.bullets", { returnObjects: true }) as string[]
                    }
                    steps={t("helpPopup:global.resellerCompleteProfile.steps", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    descriptionKey="interactionDescriptions:pages.reseller.completeProfilePopup.button.onClose"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 p-2 mt-4">
                  <GenericDropDown
                    data={pixKeyTypeOptions}
                    initialState={String(pixKeyType)}
                    handlerFuncion={(e) => setPixKeyType(Number(e.target.value) as EPixKeyType)}
                    descriptionKey="interactionDescriptions:pages.reseller.completeProfilePopup.select.pixKeyType"
                  />

                  <InputField
                    value={pixKey}
                    type="text"
                    placeholder={t("completeProfile.label_pix_key_placeholder")}
                    onChange={(e) => setPixKey(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.completeProfilePopup.input.pixKey"
                  />

                  <InputField
                    value={bankName}
                    type="text"
                    placeholder={t("completeProfile.label_bank_name_placeholder")}
                    onChange={(e) => setBankName(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.completeProfilePopup.input.bankName"
                  />

                  <InputField
                    value={bankAgency}
                    type="text"
                    placeholder={t("completeProfile.label_bank_agency_placeholder")}
                    onChange={(e) => setBankAgency(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.completeProfilePopup.input.bankAgency"
                  />

                  <InputField
                    value={bankAccount}
                    type="text"
                    placeholder={t("completeProfile.label_bank_account_placeholder")}
                    onChange={(e) => setBankAccount(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.completeProfilePopup.input.bankAccount"
                  />

                  <InputField
                    value={bankAccountHolderName}
                    type="text"
                    placeholder={t("completeProfile.label_bank_holder_name_placeholder")}
                    onChange={(e) => setBankAccountHolderName(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.completeProfilePopup.input.bankAccountHolderName"
                  />
                </div>

                <div className={defaultStyle.divisionBar}></div>

                <div className={defaultStyle.buttonsController}>
                  <Button
                    typeButton="submit"
                    label={t("completeProfile.button_save")}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.reseller.completeProfilePopup.button.save"
                  />
                </div>
              </form>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
